import { autoinject } from "aurelia-dependency-injection";
import { useView } from "aurelia-framework";
import { PortVesselsFiltersViewModel } from "./partials/mep-vessels-filters/mep-vessels-filters";
import { MepService } from "../../../../../../services/mep/mep.service";
import { I18N } from "aurelia-i18n";
import { BindingEngine, computedFrom, observable } from "aurelia-framework";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import { MepLookupService } from "../../../../../../services/mep/mep-lookup.service";
import { BaseMepFilterableCustomGrdPage } from "../../../../../../models/mep/mep.models";
import { PortVesselsOdataGridViewModel } from "./partials/mep-vessels-filters/mep-vessels-odata-grid";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { CustomerDetailsModel } from "../../../../../../models/customers/customer.models";
import KeyDownEvent = JQuery.KeyDownEvent;
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { MenuItem } from "../../../../../components/menu/menu-item";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { SignalFunctions } from "../../../../../../services/basics/signal-functions";
import { UserClientService } from "@rightship-tech/common-ui/services/basic/user-client.service";

@autoinject
@useView('views/pages/modules/mep/tabs/mep-vessels/mep-vessels.html')
export class MepVessels extends BaseMepFilterableCustomGrdPage<PortVesselsFiltersViewModel> {

    private _isAttaching: boolean = false;
    private _generatingData: boolean;

    public portId: number;
    public portName: string;
    public portUnlocode: string;
    public portVesselsOdataGridViewModel: PortVesselsOdataGridViewModel
    public customerDetails: any;
    public params: any;
    public supportsCargoType: boolean;

    //Claims
    public canDownloadPortVessels: boolean;

    @observable
    public filter: string;

    @computedFrom("item")
    get menuItems(): MenuItem[] {
        return [
            {
                click: () => this.showVesselPortActivitiesUploadHistoryDialog(),
                isVisible: true,
                item: {
                    docLinesIcon: true,
                    text: 'Vessel Data Uploads'
                },
                classList: '',
            }
        ];
    }

    //this is so that the VM is always created new 
    determineActivationStrategy() {
        return "replace";
    }

    constructor(private mepService: MepService, protected i18N: I18N, protected itemsName: string = "Port Vessels Emissions",
        protected bindingEngine: BindingEngine, private userClaimsService: UserClaimsService, private mepLookupService: MepLookupService,
        private dialogClientService: DialogClientService, private signalClientService: SignalClientService, private userClientService: UserClientService) {
        super(i18N, itemsName, bindingEngine);
        this.canDownloadPortVessels = this.userClaimsService.hasPermission(Claims.CanDownloadPortVessels);
    }

    activate(param) {
        this.params = param;
        this.portId = param.portId;
        this.portName = param.portName;
        this.portUnlocode = param.portUnlocode;
        this.supportsCargoType = param.supportsCargoType;

        if (!this.filterViewModel) {
            this.initializeFilterVM();
        }
    }

    async attached() {
        this.signalClientService.subscribe(SignalFunctions.downloadMepVesselStarted, this.onMepEmissionsDocumentStarted);
        this.signalClientService.subscribe(SignalFunctions.downloadMepVesselReady, this.onMepEmissionsDocumentReady);
        
        if (this._isAttaching) {
            return;
        }
        this._isAttaching = true
        this.dataGridVM = this.portVesselsOdataGridViewModel.dataGridVM;
        if (this.filterViewModel) {
            this.filterViewModel.portName = this.portName;
            this.filterViewModel.portUnlocode = this.portUnlocode;
        }

        await this.filterViewModel.initLookups();
        this.filter = this.filterViewModel.searchFilter;
        this._isAttaching = false;
    }

    detached() {
        this.signalClientService.unsubscribe(SignalFunctions.downloadMepVesselStarted, this.onMepEmissionsDocumentStarted);
        this.signalClientService.unsubscribe(SignalFunctions.downloadMepVesselReady, this.onMepEmissionsDocumentReady);
    }

    onMepEmissionsDocumentStarted = (data: { userId: number }) => {
        if (this.userClientService.currentUser.user.userId == data.userId) {
            this._generatingData = true;
        }
    }

    onMepEmissionsDocumentReady = (data: { userId: number }) => {
        if (this.userClientService.currentUser.user.userId == data.userId) {
            this._generatingData = false;
        }
    }

    initializeFilterVM(model?): PortVesselsFiltersViewModel {
        if (!model) {
            model = this.params ? this.params : {};
        }

        this.portVesselsOdataGridViewModel = new PortVesselsOdataGridViewModel(this.userClaimsService, this.dialogClientService);
        this.customerDetails = CustomerDetailsModel.getCustomerDetailModelFromRSUser(window["rightshipUser"]);
        this.filterViewModel = new PortVesselsFiltersViewModel(this.portVesselsOdataGridViewModel, this.dialogClientService,
            this.mepService, this.userClaimsService, this.mepLookupService, this.customerDetails, model, this.supportsCargoType);
        return this.filterViewModel;
    }

    refresh(data?: { portId: number, year: number, portName: string, portUnlocode: string, supportsCargoType: boolean }) {
        if (this.filterViewModel) {
            this.filterViewModel.clearAllFilters();
            if (data?.portId) {
                this.portId = data.portId;
                this.filterViewModel.portId = data.portId
                this.filterViewModel.supportsCargoType = data.supportsCargoType;
                this.supportsCargoType = data.supportsCargoType;
            }

            if (data?.year) {
                this.filterViewModel.selectedYear = data.year;
            }

            if (data?.portName) {
                this.portName = data.portName;
                this.filterViewModel.portName = data.portName;
            }

            if (data?.portUnlocode) {
                this.portUnlocode = data.portUnlocode;
                this.filterViewModel.portUnlocode = data.portUnlocode;
            }

            this.filterViewModel.initLookups({ portId: data.portId, year: data.year ? data.year : null, supportsCargoType: data.supportsCargoType }).then(() => {
                this.filterViewModel.applyFilters();
            });
        }
    }

    filterKeyDown($event) {
        const e: KeyDownEvent = $event.detail;

        if (e.key == "Enter") {
            this.filterViewModel.searchFilter = this.filter || '';
            this.filterViewModel.portVesselsOdataGridViewModel.refreshItemSource(this.filterViewModel.getRequest());
        }
    }

    filterChanged() {
        if (this.filterViewModel.searchFilter != this.filter) {
            if (!this.filter || this.filter.length >= 5) {
                this.filterViewModel.searchFilter = this.filter || "";
                this.filterViewModel.portVesselsOdataGridViewModel.refreshItemSource(this.filterViewModel.getRequest());
            }
        }
    }

    showVesselPortActivitiesUploadDialog() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.UpdateMepVesselPortActivitiesDialog,
            {
                filterViewModel: this.filterViewModel
            }, true, false, false, "right");
    }

    showVesselPortActivitiesUploadHistoryDialog() {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.VesselPortActivitiesUploadHistoryDialog, {
            unPortCode: this.filterViewModel.portUnlocode
        }, true, false, false, "right");
    }

    exportExcel() {
        if (this._generatingData) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.DownloadEmissionsDataDialog, {
                ports: [],
                years: [],
                generatingData: this._generatingData
            }, true, false, false, 'right');
        } else {
            if(!this._isAttaching) {
               this.filterViewModel.downloadExcel()
            }
        }
    }
}
